import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, BookOpen, Download } from "lucide-react";
import PageHeader from "@/components/site/PageHeader";
import PlaybookDialog from "@/components/site/PlaybookDialog";
import LeadDialog from "@/components/site/LeadDialog";
import { SERVICES, PROCESS } from "@/data/site";

export default function Services() {
  return (
    <main data-testid="services-page">
      <PageHeader
        overline="Services"
        title="Product engineering services for ambitious operators"
        subtitle="One technology partner across software, SaaS, AI, automation, cloud and digital transformation."
        testid="services-page-header"
      />

      <section className="py-12 md:py-16">
        <div className="container-zx grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s) => {
            const isAI = s.title === "AI & Automation Solutions";
            return (
              <div
                key={s.title}
                className={"card-surface p-7 relative " + (isAI ? "border-cyan/30 shadow-[0_18px_60px_rgba(22,213,255,0.15)]" : "")}
                data-testid={`services-card-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {isAI && (
                  <span className="absolute top-4 right-4 chip text-[10px] tracking-[0.18em] uppercase border-cyan/40 text-cyan bg-cyan/5">
                    <BookOpen className="h-3 w-3" /> Free Playbook
                  </span>
                )}
                <div className="h-11 w-11 rounded-lg grid place-items-center bg-cyan/10 border border-cyan/25 text-cyan">
                  <s.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-white">{s.title}</h3>
                <p className="mt-2 text-sm text-ink-400 leading-relaxed">{s.blurb}</p>
                <ul className="mt-5 space-y-2">
                  {["Senior engineers", "Outcome-led delivery", "Production-ready quality"].map((b) => (
                    <li key={b} className="flex items-center gap-2 text-xs text-ink-200">
                      <CheckCircle2 className="h-3.5 w-3.5 text-cyan" /> {b}
                    </li>
                  ))}
                </ul>
                {isAI && (
                  <PlaybookDialog
                    source="services_page_ai_card"
                    trigger={
                      <button
                        type="button"
                        className="mt-6 btn-secondary w-full"
                        data-testid="services-playbook-trigger"
                      >
                        <Download className="h-4 w-4" /> Get the free playbook
                      </button>
                    }
                  />
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#050F1C]/60 border-y border-white/[0.04]">
        <div className="container-zx grid lg:grid-cols-2 gap-10">
          <div>
            <div className="overline mb-3">Engagement Models</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white">Three ways to work with Zolvix</h2>
            <p className="mt-4 text-ink-200 max-w-xl">
              Whether you need a focused sprint or a long-term embedded squad, our engagement models adapt to your stage and ambition.
            </p>
            <LeadDialog
              source="services_page"
              titleVariant="expert"
              trigger={
                <button type="button" className="btn-primary mt-8" data-testid="services-contact-cta">
                  Talk to an Expert <ArrowRight className="h-4 w-4" />
                </button>
              }
            />
          </div>
          <div className="space-y-4">
            {[
              { t: "Discovery Sprint", d: "2 weeks · A working blueprint, prototype and delivery plan." },
              { t: "Product Pod", d: "Embedded cross-functional squad shipping outcomes every week." },
              { t: "Managed Platform", d: "Long-term ownership of a system — SLAs, roadmap and support." },
            ].map((m) => (
              <div key={m.t} className="card-surface p-6">
                <div className="text-sm font-semibold text-white">{m.t}</div>
                <div className="mt-1 text-sm text-ink-400">{m.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container-zx">
          <div className="overline mb-3">Our Process</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white">From discovery to scale, in six well-instrumented steps</h2>
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {PROCESS.map((p) => (
              <div key={p.step} className="card-surface p-6">
                <div className="overline">Step {p.step}</div>
                <div className="mt-1 text-lg font-semibold text-white">{p.title}</div>
                <p className="mt-2 text-sm text-ink-400">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
